"use client"

import { useCallback, useEffect, useRef, useState } from 'react'

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  // iOS / Safari
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

export function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type))
}

export class MicrophoneAccessError extends Error {
  constructor(message = 'Нет доступа к микрофону') {
    super(message)
    this.name = 'MicrophoneAccessError'
  }
}

export interface UseVoiceRecorderOptions {
  /** Мягкий лимит длительности; по достижении вызывается `onAutoStop` */
  maxDurationSec?: number
  onAutoStop?: () => void
}

export interface VoiceRecorder {
  /** Запрашивает микрофон и стартует запись. Бросает MicrophoneAccessError при отказе. */
  start: () => Promise<void>
  /** Останавливает запись и возвращает собранный Blob */
  stop: () => Promise<Blob>
  /** Останавливает запись и отбрасывает данные */
  cancel: () => void
  isRecording: boolean
  elapsedSec: number
  /** Текущий уровень громкости 0..1 (RMS), читается без ререндера — для эквалайзера */
  getLevel: () => number
}

/**
 * MediaRecorder + AnalyserNode. Треки, AudioContext и таймеры освобождаются при stop/cancel/unmount.
 * AudioContext создаётся только внутри `start()` (по user gesture), иначе Safari держит его suspended.
 */
export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): VoiceRecorder {
  const { maxDurationSec, onAutoStop } = options

  const [isRecording, setIsRecording] = useState(false)
  const [elapsedSec, setElapsedSec] = useState(0)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const dataRef = useRef<Uint8Array<ArrayBuffer> | null>(null)
  const levelRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = useRef(0)
  const stopPromiseRef = useRef<{ resolve: (b: Blob) => void; reject: (e: unknown) => void } | null>(null)
  const onAutoStopRef = useRef(onAutoStop)
  useEffect(() => { onAutoStopRef.current = onAutoStop }, [onAutoStop])
  const autoStoppedRef = useRef(false)

  const releaseResources = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (tickRef.current !== null) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    analyserRef.current?.disconnect()
    analyserRef.current = null
    dataRef.current = null
    const ctx = audioContextRef.current
    audioContextRef.current = null
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => {})
    }
    levelRef.current = 0
    recorderRef.current = null
    setIsRecording(false)
  }, [])

  const measure = useCallback(() => {
    const analyser = analyserRef.current
    const data = dataRef.current
    if (!analyser || !data) return
    analyser.getByteTimeDomainData(data)
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128
      sum += v * v
    }
    const rms = Math.sqrt(sum / data.length)
    // Речь обычно даёт RMS ~0.05–0.3 — растягиваем в 0..1
    levelRef.current = Math.min(1, rms * 4)
    rafRef.current = requestAnimationFrame(measure)
  }, [])

  const start = useCallback(async () => {
    if (recorderRef.current) return

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      throw new MicrophoneAccessError()
    }
    streamRef.current = stream

    try {
      const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AudioCtx) {
        const ctx = new AudioCtx()
        audioContextRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        analyser.smoothingTimeConstant = 0.6
        source.connect(analyser)
        analyserRef.current = analyser
        dataRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize))
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {})
        }
        rafRef.current = requestAnimationFrame(measure)
      }
    } catch {
      // Эквалайзер — некритичен: без AnalyserNode запись всё равно идёт
    }

    const mimeType = pickRecorderMimeType()
    let recorder: MediaRecorder
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    } catch {
      releaseResources()
      throw new MicrophoneAccessError('Запись не поддерживается браузером')
    }

    chunksRef.current = []
    autoStoppedRef.current = false
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type })
      chunksRef.current = []
      const pending = stopPromiseRef.current
      stopPromiseRef.current = null
      releaseResources()
      pending?.resolve(blob)
    }
    recorder.onerror = () => {
      const pending = stopPromiseRef.current
      stopPromiseRef.current = null
      releaseResources()
      pending?.reject(new MicrophoneAccessError('Ошибка записи'))
    }

    recorderRef.current = recorder
    startedAtRef.current = Date.now()
    setElapsedSec(0)
    setIsRecording(true)
    recorder.start(250)

    tickRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000)
      setElapsedSec(elapsed)
      if (maxDurationSec && elapsed >= maxDurationSec && !autoStoppedRef.current) {
        autoStoppedRef.current = true
        onAutoStopRef.current?.()
      }
    }, 250)
  }, [maxDurationSec, measure, releaseResources])

  const stop = useCallback((): Promise<Blob> => {
    const recorder = recorderRef.current
    if (!recorder) {
      return Promise.resolve(new Blob([], { type: 'audio/webm' }))
    }
    if (stopPromiseRef.current) {
      // Повторный stop — возвращаем тот же результат
      return new Promise((resolve, reject) => {
        const prev = stopPromiseRef.current!
        stopPromiseRef.current = {
          resolve: (b) => { prev.resolve(b); resolve(b) },
          reject: (e) => { prev.reject(e); reject(e) },
        }
      })
    }
    return new Promise<Blob>((resolve, reject) => {
      stopPromiseRef.current = { resolve, reject }
      if (recorder.state === 'inactive') {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        chunksRef.current = []
        stopPromiseRef.current = null
        releaseResources()
        resolve(blob)
        return
      }
      try {
        recorder.stop()
      } catch (error) {
        stopPromiseRef.current = null
        releaseResources()
        reject(error)
      }
    })
  }, [releaseResources])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    stopPromiseRef.current = null
    chunksRef.current = []
    if (recorder) {
      recorder.ondataavailable = null
      recorder.onstop = null
      recorder.onerror = null
      try {
        if (recorder.state !== 'inactive') recorder.stop()
      } catch {
        // уже остановлен
      }
    }
    releaseResources()
  }, [releaseResources])

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current
      if (recorder) {
        recorder.ondataavailable = null
        recorder.onstop = null
        recorder.onerror = null
        try {
          if (recorder.state !== 'inactive') recorder.stop()
        } catch {
          // ignore
        }
      }
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      if (tickRef.current !== null) clearInterval(tickRef.current)
      streamRef.current?.getTracks().forEach((track) => track.stop())
      const ctx = audioContextRef.current
      if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {})
    }
  }, [])

  const getLevel = useCallback(() => levelRef.current, [])

  return { start, stop, cancel, isRecording, elapsedSec, getLevel }
}
